import { useCallback, useEffect, useRef } from 'react';
import TooltipV2 from '@/components/TooltipV2';
import { saveClipChanges } from '@/stores/clip';
import { useClipPendingUpdate } from '@/hooks/useClipPendingUpdate';
import { ClipSyncStatus } from '@/stores/pendingEditsStore';
import { useBeforeUnload } from '@/hooks/useBeforeUnload';
import { getClipAnalyticsPayload } from '@/hooks/utils/analyticsHookUtil';
import Icon from '@/components/atoms/Icon';

const size = 20;

const ClipUpdateInProgress = ({ clipId }: { clipId: string }) => {
  const { status } = useClipPendingUpdate({ clipId });

  const shouldAttemptRetrySaving = useRef(false);
  shouldAttemptRetrySaving.current = status === ClipSyncStatus.sync_failed;

  const retrySavingBeforeUnload = useCallback(() => {
    saveClipChanges({
      clipId,
      onFailure: ({ error, clip }) => {
        window.logger?.error('LAST_SAVE_ATTEMPT_FAILED', error);
        window.analytics?.track('ContentLabEvent', {
          name: 'LAST_SAVE_ATTEMPT_FAILED',
          isSuccess: false,
          ...getClipAnalyticsPayload(clip)
        });
      }
    });
  }, [clipId]);

  useEffect(() => {
    return () => {
      if (shouldAttemptRetrySaving.current) {
        retrySavingBeforeUnload();
      }
    };
  }, [retrySavingBeforeUnload]);

  useBeforeUnload({ shouldPrompt: shouldAttemptRetrySaving.current, action: retrySavingBeforeUnload });

  return (
    <div className="z-[100]">
      {status === ClipSyncStatus.sync_in_progress ? (
        <Icon name="IconLoader2" className="animate-spin" size={size} />
      ) : status === ClipSyncStatus.sync_failed ? (
        <TooltipV2
          tooltipId="edits-out-of-sync"
          tooltipContent="Changes not saved"
          tooltipElement={<Icon name="IconCloudCancel" color="red" size={size} />}
        />
      ) : (
        <div className="pt-1">
          <TooltipV2
            tooltipId="edits-saved"
            tooltipContent="All changes saved"
            tooltipElement={<Icon name="IconCloudCheck" size={size} />}
          />
        </div>
      )}
    </div>
  );
};

export default ClipUpdateInProgress;
